import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import RecipientPicker from './RecipientPicker';
import CircularProgress from '@mui/material/CircularProgress';
import { motion } from 'framer-motion';
import { ArrowLeft, Send, Megaphone } from 'lucide-react';

// Presentational: layout only, all state lives in BroadcastComposerContainer.
// No client-side message length validation — same convention as MessageInput,
// which relies entirely on the backend's 4096-char rejection.
const BroadcastComposer = ({
  onBack,
  students,
  recipientsLoading,
  selectedIds,
  onToggleRecipient,
  onSelectAll,
  onClearAll,
  maxRecipients,
  text,
  onTextChange,
  sending,
  error,
  confirming,
  onRequestSend,
  onConfirmSend,
  onCancelConfirm,
}) => {
  const theme = useTheme();
  const isDark = theme?.mode === 'dark';
  const accentColor = theme?.colors?.button_light || '#fdbf00';
  const primaryColor = theme?.colors?.primary_light || '#1a4275';
  const borderCol = isDark ? 'rgba(51, 65, 85, 0.4)' : '#e2e8f0';
  const textPrimary = isDark ? '#f8fafc' : '#1e293b';

  const overCap = selectedIds.length > maxRecipients;
  const canSend = selectedIds.length > 0 && !overCap && text.trim().length > 0 && !sending;

  return (
    <div className="w-full flex flex-col h-full overflow-hidden relative">
      {/* Header */}
      <div
        className="p-4 flex items-center gap-3.5 border-b shadow-sm transition-colors duration-300"
        style={{ borderColor: borderCol, backgroundColor: isDark ? '#162542' : '#ffffff' }}
      >
        <button
          onClick={onBack}
          className="p-2 rounded-full hover:bg-slate-100/80 dark:hover:bg-slate-800/50 transition-colors"
          style={{ color: accentColor }}
        >
          <ArrowLeft size={22} />
        </button>
        <div className="flex items-center gap-2">
          <Megaphone size={20} style={{ color: accentColor }} />
          <h3 className="font-bold text-xl m-0" style={{ color: textPrimary }}>
            Nuevo anuncio
          </h3>
        </div>
      </div>

      {error && (
        <div className="bg-rose-500/10 text-rose-500 p-2.5 text-base font-medium text-center border-b border-rose-500/20">
          {error}
        </div>
      )}

      {/* Recipients */}
      <div className="flex-1 overflow-hidden flex flex-col min-h-0" style={{ flexBasis: '55%' }}>
        <RecipientPicker
          students={students}
          selectedIds={selectedIds}
          onToggle={onToggleRecipient}
          onSelectAll={onSelectAll}
          onClearAll={onClearAll}
          maxRecipients={maxRecipients}
          loading={recipientsLoading}
        />
      </div>

      {/* Message + send */}
      <div className="p-3 border-t transition-colors duration-300" style={{ backgroundColor: isDark ? '#111e35' : '#f8fafc', borderColor: borderCol }}>
        <div className="flex items-end gap-3 max-w-full relative">
          <textarea
            rows={2}
            value={text}
            onChange={(e) => onTextChange(e.target.value)}
            placeholder="Escribe el mensaje del anuncio..."
            disabled={sending}
            className={`flex-1 rounded-2xl border px-4 py-3 text-xl focus:outline-none transition-all duration-300 custom-scrollbar resize-none ${
              isDark
                ? 'bg-slate-800 text-slate-100 placeholder-slate-500 focus:ring-1 focus:ring-slate-600 focus:bg-slate-800'
                : 'bg-white text-slate-800 placeholder-slate-400 focus:ring-1 focus:ring-slate-300 focus:bg-white'
            }`}
            style={{ borderColor: borderCol, maxHeight: '120px', minHeight: '56px', lineHeight: '1.4' }}
          />

          <motion.button
            type="button"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            disabled={!canSend}
            onClick={onRequestSend}
            className="rounded-2xl w-11 h-11 flex items-center justify-center text-white disabled:opacity-40 transition-colors shadow-md flex-shrink-0 cursor-pointer"
            style={{ backgroundColor: accentColor, minWidth: '44px', minHeight: '44px' }}
          >
            {sending ? <CircularProgress size={18} color="inherit" /> : <Send size={18} style={{ marginLeft: '1.5px' }} />}
          </motion.button>
        </div>

        <div className="mt-2 text-base font-semibold" style={{ color: overCap ? '#ef4444' : (isDark ? '#94a3b8' : '#64748b') }}>
          {selectedIds.length} seleccionados / {maxRecipients} máx
        </div>
      </div>

      {/* Confirm step — sending to up to 40 people is irreversible (no delete). */}
      {confirming && (
        <div className="absolute inset-0 flex items-center justify-center p-6" style={{ backgroundColor: 'rgba(15, 23, 42, 0.55)' }}>
          <div
            className="w-full max-w-sm rounded-2xl p-6 shadow-xl space-y-4"
            style={{ backgroundColor: isDark ? '#1e293b' : '#ffffff' }}
          >
            <h4 className="text-xl font-bold m-0" style={{ color: textPrimary }}>
              ¿Enviar este mensaje a {selectedIds.length} alumnos?
            </h4>
            <p className="text-lg m-0" style={{ color: isDark ? '#94a3b8' : '#64748b' }}>
              Esta acción no se puede deshacer.
            </p>
            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={onCancelConfirm}
                disabled={sending}
                className="px-4 py-2 rounded-xl font-semibold text-lg"
                style={{ color: isDark ? '#e2e8f0' : '#475569' }}
              >
                Cancelar
              </button>
              <button
                onClick={onConfirmSend}
                disabled={sending}
                className="px-4 py-2 rounded-xl font-semibold text-lg text-white flex items-center gap-2"
                style={{ backgroundColor: primaryColor }}
              >
                {sending && <CircularProgress size={16} color="inherit" />}
                Enviar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BroadcastComposer;
